import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../core/environments/environment';
import {
  colorEndpoint,
  ColorSystem,
  ColorSystemByIdResponse,
  ColorSystemsResponse,
} from '../models/color-systems.model';
import { Observable, forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ColorSystemsService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;

  private readonly COLOR_TARGETS = new Set([1, 2, 13, 8, 7]);
  private readonly COLOR_STORAGE_KEY = 'app_colors_system';

  getColorTargets(): number[] {
    return Array.from(this.COLOR_TARGETS);
  }

  fetchAllColors(): Observable<ColorSystemsResponse> {
    return this._http.get<ColorSystemsResponse>(
      `${this._apiUrl}/${colorEndpoint.getColors}`,
    );
  }

  fetchColorById(id: number): Observable<ColorSystemByIdResponse> {
    return this._http.get<ColorSystemByIdResponse>(
      `${this._apiUrl}/${colorEndpoint.getColors}/${id}`,
    );
  }

  fetchColorByIds(ids: number[]): Observable<ColorSystemByIdResponse[]> {
    const requests = ids.map((id) => this.fetchColorById(id));
    return forkJoin(requests);
  }

  updateColorById(
    id: number,
    colorSystem: ColorSystem,
  ): Observable<ColorSystemByIdResponse> {
    return this._http.put<ColorSystemByIdResponse>(
      `${this._apiUrl}/${colorEndpoint.updateColorById}/${id}`,
      colorSystem,
    );
  }

  applyCachedColors(): void {
    const cached = localStorage.getItem(this.COLOR_STORAGE_KEY);
    if (!cached) return;

    try {
      const data = JSON.parse(cached);
      this.applyColors(data);
    } catch (error) {
      console.warn('Error parsing cached colors', error);
      localStorage.removeItem(this.COLOR_STORAGE_KEY);
    }
  }

  loadAndApplyColors(): void {
    this.fetchAllColors().subscribe({
      next: (res) => {
        const data = res?.data ?? [];
        this.setColors(data);
      },
      error: () => {
        console.error('Error loading and applying theme colors');
      },
    });
  }

  setColors(colors: ColorSystem[]): void {
    this.applyColors(colors);
    localStorage.setItem(this.COLOR_STORAGE_KEY, JSON.stringify(colors));
  }

  applyColors(data: ColorSystem[]): void {
    const root = document.documentElement;

    for (const c of data) {
      if (this.COLOR_TARGETS.has(c.color_id)) {
        root.style.setProperty(c.color_name_value, c.color_code);
      }
    }
  }
}
